import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";
import { convertAnestesiData } from "@/store/modules/operation/anestesiModule";

export type Premedicinering = {
    id: number;
    namn: string;
    artikelnummer: string;
    ordning: number;
    anestesi_id: number;
    lakemedelsartikel_id: number;
};

export type PremedicineringDisplay = {
    value: Premedicinering["id"];
    label: string;
};

type State = BaseState & {
    premedicinering: Map<Premedicinering["id"], Premedicinering>;
};

type Getters = {
    premedicineringById: (data: { id: number }) => Premedicinering | null;
};

export const convertPremedicineringData: (args: {
    premedicinering?: Premedicinering;
}) => PremedicineringDisplay | null = ({ premedicinering }) => {
    if (!premedicinering) {
        return null;
    }

    return {
        value: premedicinering.id,
        label: premedicinering.namn,
    };
};

const getDefaultState: () => State = () => {
    return {
        premedicinering: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "premedicinering";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        premedicinering: (state: State) => [...state.premedicinering.values()],
        premedicineringSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertPremedicineringData({ premedicinering: singleResult }));
            }

            return result;
        },
        premedicineringById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }
                return state.premedicinering.get(id);
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addPremedicinering: (
            state: State,
            {
                offset,
                subsetName = "default",
                premedicinering = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                premedicinering: Premedicinering[];
            }
        ) =>
            subsetSetter<State, Premedicinering>({
                offset,
                subsetName,
                state,
                collectionName: moduleName,
                items: premedicinering,
            }),
        updatePremedicinering: (
            state: State,
            {
                premedicinering,
                offset,
                subsetName,
            }: {
                premedicinering: Premedicinering[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Premedicinering>({
                offset,
                state,
                collectionName: moduleName,
                items: premedicinering,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        getPremedicineringById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
            }: {
                id: number;
            }
        ) => {
            const foundPremedicinering = getters.premedicineringById({ id });

            if (foundPremedicinering || !id) {
                return foundPremedicinering;
            }

            try {
                const { data } = await klinikenApi.get<{
                    premedicinering: Premedicinering;
                }>(`operation/premedicinering/${id}`, getConfig({}));

                if (data.premedicinering) {
                    const retrievedPremedicinering = data.premedicinering;

                    commit("addPremedicinering", {
                        offset: 0,
                        premedicinering: [retrievedPremedicinering],
                        subsetName: id,
                    });

                    return retrievedPremedicinering;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        fetchPremedicinering: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams,
                forceRefresh = false,
                displayMode,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
                forceRefresh?: boolean;
                displayMode?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Premedicinering>({
                    filterParams,
                    state,
                    limit,
                    page,
                    forceRefresh,
                    collectionName: moduleName,
                    apiUrl: "operation/premedicinering/",
                });

                if (!results) {
                    if (typeof total === "number") {
                        commit("setTotal", {
                            total,
                            subsetName,
                        });
                    }

                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addPremedicinering", {
                    offset,
                    subsetName,
                    premedicinering: results,
                });
                commit("setTotal", {
                    total,
                    subsetName,
                });

                return {
                    subsetName,
                    total,
                    nextPage,
                    results: displayMode
                        ? results.map((singleResult) => convertPremedicineringData({ premedicinering: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createPremedicinering: async (
            { commit, state }: BaseActionsProps<State>,
            {
                premedicinering,
            }: {
                premedicinering: Premedicinering;
            }
        ) => {
            try {
                const response = await klinikenApi.post("operation/premedicinering/", premedicinering, getConfig({}));

                commit("addPremedicinering", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    premedicinering: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updatePremedicinering: async (
            { commit, state }: BaseActionsProps<State>,
            {
                premedicinering,
            }: {
                premedicinering: Premedicinering;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `operation/premedicinering/${premedicinering.id}/`,
                    premedicinering,
                    getConfig({})
                );

                commit("updatePremedicinering", {
                    subsetName: "newlyUpdated",
                    premedicinering: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
